import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Droplet, Truck, Boxes, Recycle, ShieldAlert, Check } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchSousProduits,
  fetchCuves,
  fetchArticles,
  ajouterEpandage,
  ajouterExpeditionGrignons,
  ajusterTonnageGrignons,
  facturerGrignons,
  ajouterLotNoyaux,
  basculerFactureNoyaux,
  declarerRepassage,
  annulerRepassage,
  productionPeriodeRepassage,
  bornePrecedenteRepassage,
  compteurRepassage,
  repassagesAValoriserZero,
  corrigerValorisationRepassage,
  valeurMatiere,
  TAUX_GRIGNON_2PHASES,
} from '../lib/dataProvider';
import { fmt, PageTitle, StatBlock } from '../components/shared/badges';
import { useAuth } from '../lib/auth';
import { attribuerNumero } from '../lib/souches';
import { PrintDoc } from '../components/print/PrintDoc';
import { FacturationSousProduits } from '../components/sousproduits/FacturationSousProduits';
import { voitMontants } from '../lib/permissions';

const inputCls =
  'h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

const Section = ({ testid, icon: Icon, titre, sous, accent, children }) => (
  <section data-testid={testid} className={`border border-slate-200 border-l-2 bg-white ${accent}`}>
    <div className="border-b border-slate-200 px-4 py-3">
      <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
        <Icon size={16} /> {titre}
      </h2>
      <p className="text-xs text-slate-500">{sous}</p>
    </div>
    {children}
  </section>
);

const StatutGrignon = ({ statut }) => (
  <span
    className={`inline-block border px-1.5 py-0.5 font-data text-[11px] uppercase tracking-[0.06em] ${
      statut === 'Facturé'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
        : statut === 'Pesé'
          ? 'border-sky-200 bg-sky-50 text-sky-700'
          : 'border-amber-200 bg-amber-50 text-amber-700'
    }`}
  >
    {statut}
  </span>
);

export default function SousProduits() {
  const { user } = useAuth();
  const argent = voitMontants(user?.role);
  const [bonPesee, setBonPesee] = useState(null);
  const [sp, setSp] = useState(null);
  const [cuves, setCuves] = useState([]);
  const [articles, setArticles] = useState([]);
  const aujourdhui = new Date().toISOString().slice(0, 10);

  const [ep, setEp] = useState({ date: aujourdhui, chauffeur: '', citernes: '', destination: '' });
  const [gr, setGr] = useState({ date: aujourdhui, acheteur: 'CHO Company', transporteur: '', camions: '', prix: '' });
  const [no, setNo] = useState({ date: aujourdhui, acheteur: 'SOTICI Biomasse', transporteur: '', matricule: '', bl: '', poids: '', prix: '' });
  // Saisie locale usine (Africa/Tunis) ↔ horodatage non ambigu stocké en UTC.
  const versLocal = (iso) => (iso ? new Date(new Date(iso).getTime() + 3600000).toISOString().slice(0, 16) : '');
  const [rp, setRp] = useState({
    date: aujourdhui,
    huileGrignon: '',
    cuveId: '',
    periodeDebut: '',
    periodeFin: '',
    coefficientGrignon: TAUX_GRIGNON_2PHASES,
  });
  const [occupe, setOccupe] = useState(false);
  const [motifCorrection, setMotifCorrection] = useState('');
  const borneContinuite = bornePrecedenteRepassage();
  const production = useMemo(
    () => productionPeriodeRepassage(rp.periodeDebut, rp.periodeFin),
    [rp.periodeDebut, rp.periodeFin]
  );
  const coef = Number(rp.coefficientGrignon) > 0 ? Number(rp.coefficientGrignon) : TAUX_GRIGNON_2PHASES;
  const grignonsEstimes = Number(((production.kg * coef) / 1000).toFixed(3));
  const rdtRepassage = production.kg > 0 && Number(rp.huileGrignon) > 0 ? (Number(rp.huileGrignon) / production.kg) * 100 : null;
  const compteur = compteurRepassage();
  const aCorriger = repassagesAValoriserZero();
  const [tonnages, setTonnages] = useState({});

  const load = useCallback(() => {
    fetchSousProduits().then(setSp);
    fetchCuves().then(setCuves);
  }, []);

  useEffect(() => {
    load();
    fetchArticles().then(setArticles);
  }, [load]);

  // Préremplissage du « DU » : borne « AU » la plus récente des opérations validées
  // et non annulées (continuité des plages), jamais le dernier enregistrement inséré.
  useEffect(() => {
    if (borneContinuite && !rp.periodeDebut) setRp((p) => ({ ...p, periodeDebut: versLocal(borneContinuite) }));
  }, [borneContinuite, rp.periodeDebut]);

  const cuvesLampantes = useMemo(() => cuves.filter((c) => c.famille === 'lampante'), [cuves]);
  const lampanteTotal = cuvesLampantes.reduce((s, c) => s + c.volume, 0);
  const lampanteValeur = cuvesLampantes.reduce((s, c) => s + valeurMatiere(c), 0);

  if (!sp) return null;

  const aFacturer = sp.grignons.filter((g) => g.statut === 'Pesé');
  const totalGrignonsFacturable = aFacturer.reduce((s, g) => s + (g.tonnage || 0) * g.prix, 0);

  const submitEp = async (e) => {
    e.preventDefault();
    if (!ep.chauffeur.trim()) return toast.error('Saisissez le chauffeur / transporteur');
    if (!Number(ep.citernes)) return toast.error('Saisissez le nombre de citernes');
    await ajouterEpandage({ ...ep, chauffeur: ep.chauffeur.trim(), citernes: Number(ep.citernes), destination: ep.destination.trim() || '—' });
    toast.success('Évacuation de margines enregistrée');
    setEp({ date: aujourdhui, chauffeur: '', citernes: '', destination: '' });
    load();
  };

  const submitGr = async (e) => {
    e.preventDefault();
    if (!gr.transporteur.trim()) return toast.error('Saisissez le transporteur');
    if (!Number(gr.camions)) return toast.error('Saisissez le nombre de semi-remorques');
    await ajouterExpeditionGrignons({
      ...gr,
      transporteur: gr.transporteur.trim(),
      camions: Number(gr.camions),
      ...(Number(gr.prix) > 0 ? { prix: Number(gr.prix) } : {}),
    });
    toast.success('Expédition enregistrée — tonnage à ajuster après retour industriel');
    setGr({ date: aujourdhui, acheteur: 'CHO Company', transporteur: '', camions: '', prix: '' });
    load();
  };

  const submitNo = async (e) => {
    e.preventDefault();
    if (!no.transporteur.trim() || !no.matricule.trim()) return toast.error('Transporteur et matricule obligatoires');
    if (!Number(no.poids)) return toast.error('Saisissez le poids (T)');
    await ajouterLotNoyaux({
      ...no,
      transporteur: no.transporteur.trim(),
      matricule: no.matricule.trim(),
      bl: no.bl.trim() || '—',
      poids: Number(no.poids),
      ...(Number(no.prix) > 0 ? { prix: Number(no.prix) } : {}),
    });
    toast.success('Lot de noyaux enregistré');
    setNo({ date: aujourdhui, acheteur: 'SOTICI Biomasse', transporteur: '', matricule: '', bl: '', poids: '', prix: '' });
    load();
  };

  const submitRp = async (e) => {
    e.preventDefault();
    if (occupe) return;
    setOccupe(true);
    const { error, cuve, row, dejaEnregistre } = await declarerRepassage({
      ...rp,
      coefficientGrignon: coef,
      motifCorrection: motifCorrection.trim() || null,
      utilisateur: user?.name,
    });
    setOccupe(false);
    if (error) return toast.error(error);
    toast.success(
      dejaEnregistre
        ? `Opération déjà enregistrée (${row.id}) — aucun doublon de stock créé.`
        : `${fmt(rp.huileGrignon)} ${cuve.unite} d'huile de repassage versés dans ${cuve.nom} — coût matière 0 TND/kg`
    );
    setRp({
      date: aujourdhui,
      huileGrignon: '',
      cuveId: '',
      periodeDebut: versLocal(row.periodeFin),
      periodeFin: '',
      coefficientGrignon: coef,
    });
    setMotifCorrection('');
    load();
  };

  const annuler = async (id) => {
    const { error } = await annulerRepassage(id, user?.name);
    if (error) return toast.error(error);
    toast.success('Opération annulée : kilos retirés de la cuve, aucune valeur créée');
    load();
  };

  const corriger = async (id) => {
    const { error } = await corrigerValorisationRepassage(id, user?.name);
    if (error) return toast.error(error);
    toast.success('Valorisation historique ramenée à 0 TND (correction tracée)');
    load();
  };

  const ajuster = async (id) => {
    const { error } = await ajusterTonnageGrignons(id, tonnages[id]);
    if (error) return toast.error(error);
    toast.success('Tonnage ajusté');
    setTonnages((t) => ({ ...t, [id]: '' }));
    load();
  };

  return (
    <div data-testid="sous-produits-page" className="space-y-6">
      <PageTitle
        emoji="♻️"
        titre="Sous-Produits & Huile de Grignon"
        sous="Margines, grignons, noyaux, ligne de repassage et stock d'emballages"
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <StatBlock
          testid="stat-margines"
          titre="Margines évacuées"
          accent="border-l-sky-700"
          items={[
            ['Sorties', fmt(sp.epandages.length)],
            ['Citernes', fmt(sp.epandages.reduce((s, e) => s + e.citernes, 0))],
          ]}
        />
        <StatBlock
          testid="stat-grignons"
          titre="Grignons"
          accent="border-l-amber-600"
          items={[
            ['Stock (T)', fmt(sp.stocks.grignons, 1)],
            ['Camions', fmt(sp.grignons.reduce((s, g) => s + g.camions, 0))],
            ['À peser', fmt(sp.grignons.filter((g) => g.statut === 'À peser').length)],
          ]}
        />
        <StatBlock
          testid="stat-noyaux"
          titre="Noyaux (biomasse)"
          accent="border-l-slate-900"
          items={[
            ['Lots', fmt(sp.noyaux.length)],
            ['Poids (T)', fmt(sp.noyaux.reduce((s, n) => s + n.poids, 0), 1)],
            ['Non facturés', fmt(sp.noyaux.filter((n) => !n.facture).length)],
          ]}
        />
        <StatBlock
          testid="stat-lampante"
          titre="Stock lampante (repassage)"
          accent="border-l-orange-500"
          items={[
            ['Volume', fmt(lampanteTotal)],
            ...(argent ? [['Valeur CMP (TND)', fmt(lampanteValeur)]] : []),
          ]}
        />
      </div>

      <Section
        testid="section-margines"
        icon={Droplet}
        accent="border-l-sky-700"
        titre="Margines — Épandage"
        sous="Suivi logistique quotidien de l'évacuation vers les champs (sous les oliviers)"
      >
        <form onSubmit={submitEp} className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 md:grid-cols-5">
          <input data-testid="ep-date" type="date" value={ep.date} onChange={(e) => setEp({ ...ep, date: e.target.value })} className={inputCls} />
          <input data-testid="ep-chauffeur" placeholder="Chauffeur / transporteur" value={ep.chauffeur} onChange={(e) => setEp({ ...ep, chauffeur: e.target.value })} className={inputCls} />
          <input data-testid="ep-citernes" type="number" min="0" placeholder="Nb citernes" value={ep.citernes} onChange={(e) => setEp({ ...ep, citernes: e.target.value })} className={inputCls} />
          <input data-testid="ep-destination" placeholder="Parcelle / destination" value={ep.destination} onChange={(e) => setEp({ ...ep, destination: e.target.value })} className={inputCls} />
          <button type="submit" data-testid="ep-submit" className="flex h-10 items-center justify-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700">
            <Plus size={15} /> Enregistrer
          </button>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-margines">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date', 'Chauffeur / transporteur', 'Citernes évacuées', 'Destination'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sp.epandages.map((e) => (
                <tr key={e.id} data-testid={`epandage-row-${e.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{e.date}</td>
                  <td className="px-3 py-2 text-sm text-slate-800">{e.chauffeur}</td>
                  <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(e.citernes)}</td>
                  <td className="px-3 py-2 text-sm text-slate-600">{e.destination}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        testid="section-grignons"
        icon={Truck}
        accent="border-l-amber-600"
        titre="Grignons — Vente industrielle"
        sous="Sorties de semi-remorques sans pesée initiale, tonnage ajustable a posteriori pour facturation globale"
      >
        <form onSubmit={submitGr} className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 md:grid-cols-5">
          <input data-testid="gr-date" type="date" value={gr.date} onChange={(e) => setGr({ ...gr, date: e.target.value })} className={inputCls} />
          <input data-testid="gr-acheteur" placeholder="Acheteur industriel" value={gr.acheteur} onChange={(e) => setGr({ ...gr, acheteur: e.target.value })} className={inputCls} />
          <input data-testid="gr-transporteur" placeholder="Transporteur" value={gr.transporteur} onChange={(e) => setGr({ ...gr, transporteur: e.target.value })} className={inputCls} />
          <input data-testid="gr-camions" type="number" min="0" placeholder="Nb semi-remorques" value={gr.camions} onChange={(e) => setGr({ ...gr, camions: e.target.value })} className={inputCls} />
          <input data-testid="gr-prix" type="number" step="0.001" min="0" placeholder="Prix (TND/T)" value={gr.prix} onChange={(e) => setGr({ ...gr, prix: e.target.value })} className={inputCls} />
          <button type="submit" data-testid="gr-submit" className="flex h-10 items-center justify-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700">
            <Plus size={15} /> Expédier
          </button>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-grignons">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date', 'Acheteur', 'Transporteur', 'Camions', 'Tonnage (T)', ...(argent ? ['Montant (TND)'] : []), 'Statut', 'Ajustement', 'Bon'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sp.grignons.map((g) => (
                <tr key={g.id} data-testid={`grignon-row-${g.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{g.date}</td>
                  <td className="px-3 py-2 text-sm text-slate-800">{g.acheteur}</td>
                  <td className="px-3 py-2 text-sm text-slate-700">{g.transporteur}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(g.camions)}</td>
                  <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">
                    {g.tonnage ? fmt(g.tonnage, 1) : <span className="text-amber-700">en attente</span>}
                  </td>
                  {argent && (
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-900">
                      {g.tonnage ? fmt(g.tonnage * g.prix, 2) : '—'}
                    </td>
                  )}
                  <td className="px-3 py-2"><StatutGrignon statut={g.statut} /></td>
                  <td className="px-3 py-2">
                    {g.statut === 'Facturé' ? (
                      <span className="font-data text-xs text-slate-400">clôturé</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <input
                          data-testid={`gr-tonnage-${g.id}`}
                          type="number"
                          step="0.1"
                          placeholder="T"
                          value={tonnages[g.id] ?? ''}
                          onChange={(e) => setTonnages((t) => ({ ...t, [g.id]: e.target.value }))}
                          className="tabular h-8 w-20 border border-slate-300 bg-slate-50 px-2 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                        />
                        <button
                          type="button"
                          data-testid={`gr-ajuster-${g.id}`}
                          onClick={() => ajuster(g.id)}
                          className="border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                        >
                          Ajuster
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      data-testid={`gr-bon-${g.id}`}
                      disabled={!g.tonnage}
                      onClick={() => setBonPesee(g)}
                      className="border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white disabled:border-slate-200 disabled:text-slate-300 disabled:hover:bg-transparent"
                    >
                      Bon de pesée
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <p className="font-data text-xs text-slate-600">
            Facturation globale de fin de période — {fmt(aFacturer.length)} lot(s) pesé(s)
            {argent ? ` · ${fmt(totalGrignonsFacturable, 2)} TND` : ''}
          </p>
          <button
            type="button"
            data-testid="gr-facturer"
            onClick={async () => {
              if (!aFacturer.length) return toast.error('Aucun lot pesé à facturer');
              await facturerGrignons(aFacturer.map((g) => g.id));
              toast.success('Facturation globale enregistrée');
              load();
            }}
            className="h-10 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            Facturer la période
          </button>
        </div>
      </Section>

      <Section
        testid="section-noyaux"
        icon={Boxes}
        accent="border-l-slate-900"
        titre="Noyaux — Biomasse"
        sous="Acheteur, transporteur, matricule, bon de livraison, poids et facturations différées"
      >
        <form onSubmit={submitNo} className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 md:grid-cols-7">
          <input data-testid="no-date" type="date" value={no.date} onChange={(e) => setNo({ ...no, date: e.target.value })} className={inputCls} />
          <input data-testid="no-acheteur" placeholder="Acheteur" value={no.acheteur} onChange={(e) => setNo({ ...no, acheteur: e.target.value })} className={inputCls} />
          <input data-testid="no-transporteur" placeholder="Transporteur" value={no.transporteur} onChange={(e) => setNo({ ...no, transporteur: e.target.value })} className={inputCls} />
          <input data-testid="no-matricule" placeholder="Matricule véhicule" value={no.matricule} onChange={(e) => setNo({ ...no, matricule: e.target.value })} className={inputCls} />
          <input data-testid="no-bl" placeholder="Bon de livraison" value={no.bl} onChange={(e) => setNo({ ...no, bl: e.target.value })} className={inputCls} />
          <input data-testid="no-poids" type="number" step="0.1" placeholder="Poids (T)" value={no.poids} onChange={(e) => setNo({ ...no, poids: e.target.value })} className={inputCls} />
          <input data-testid="no-prix" type="number" step="0.001" min="0" placeholder="Prix (TND/T)" value={no.prix} onChange={(e) => setNo({ ...no, prix: e.target.value })} className={inputCls} />
          <button type="submit" data-testid="no-submit" className="flex h-10 items-center justify-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700">
            <Plus size={15} /> Ajouter
          </button>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-noyaux">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date', 'Acheteur', 'Transporteur', 'Matricule', 'BL', 'Poids (T)', ...(argent ? ['Montant (TND)'] : []), 'Facturation'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sp.noyaux.map((n) => (
                <tr key={n.id} data-testid={`noyau-row-${n.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{n.date}</td>
                  <td className="px-3 py-2 text-sm text-slate-800">{n.acheteur}</td>
                  <td className="px-3 py-2 text-sm text-slate-700">{n.transporteur}</td>
                  <td className="px-3 py-2 font-data text-sm text-slate-900">{n.matricule}</td>
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{n.bl}</td>
                  <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(n.poids, 1)}</td>
                  {argent && <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(n.poids * n.prix, 2)}</td>}
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid={`no-facture-${n.id}`}
                      onClick={async () => {
                        await basculerFactureNoyaux(n.id);
                        load();
                      }}
                      className={`inline-flex items-center gap-1 border px-2 py-1 font-data text-[11px] uppercase tracking-[0.06em] transition-colors duration-150 ${
                        n.facture
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-amber-200 bg-amber-50 text-amber-700'
                      }`}
                    >
                      {n.facture ? <Check size={12} /> : null} {n.facture ? 'Facturé' : 'Différé'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        testid="section-repassage"
        icon={Recycle}
        accent="border-l-orange-500"
        titre="Ligne de repassage & Huile de grignon"
        sous="Production quotidienne issue des grignons de première phase — routage étanche vers la filière lampante"
      >
        <p data-testid="repassage-routage" className="flex items-start gap-2 border-b border-orange-200 bg-orange-50 px-4 py-3 text-xs text-orange-900">
          <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            <span className="font-bold uppercase tracking-[0.06em]">Routage obligatoire</span> — l'huile de
            grignon est affectée exclusivement aux cuves LAMPANTES. Aucune cuve noble n'est proposée. Le coût des
            olives reste entièrement affecté à la première extraction :{' '}
            <span className="font-bold">aucun coût matière n'est affecté à l'huile récupérée</span> (coût matière
            0 TND/kg, valeur matière d'entrée 0 TND). Le stock physique augmente, le CMP Achat pur et la valeur
            matière des achats restent inchangés. Aucune dépense, dette fournisseur, sortie de caisse ni recette
            n'est créée ; les frais industriels restent suivis séparément.
          </span>
        </p>
        <div data-testid="rp-compteur" className="flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-slate-200 bg-white px-4 py-3">
          <p className="font-data text-sm font-bold text-slate-900">
            Huile de repassage produite — campagne : {fmt(compteur.kg)} kg · coût matière : 0 TND/kg
          </p>
          <p data-testid="rp-rendement-campagne" className="border-2 border-orange-600 bg-orange-50 px-2 py-0.5 font-data text-sm font-bold text-orange-800">
            Rendement de repassage cumulé : {compteur.rendement != null ? `${fmt(compteur.rendement, 2)} %` : '—'}
            <span className="ml-1 text-[10px] font-normal text-slate-600">
              {compteur.olives > 0 ? `sur ${fmt(compteur.olives)} kg d’olives d’origine` : 'aucune olive d’origine retenue'}
            </span>
          </p>
          <p className="text-[11px] text-slate-500">
            {compteur.operations} opération(s) validée(s){compteur.annulees ? ` · ${compteur.annulees} annulée(s) déduite(s)` : ''} — ce
            compteur n'est pas le stock restant après vente et ne constitue pas une deuxième entrée physique : ces
            kilos sont déjà dans la cuve.
          </p>
        </div>
        {aCorriger.length > 0 && (
          <div data-testid="rp-historique-a-corriger" className="border-b border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-amber-900">
              Opérations historiques valorisées — correction proposée ({aCorriger.length})
            </p>
            <p className="mt-0.5 text-[11px] text-amber-900">
              Ces entrées avaient été valorisées au CMP de la cuve (ou amorcées à 4,200 TND/kg). Rien n'est remis à
              zéro automatiquement : la correction est explicite et tracée.
            </p>
            <ul className="mt-2 space-y-1">
              {aCorriger.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-2 text-[11px] text-amber-900">
                  <span className="font-data">
                    {p.id} · {p.date} · {p.cuveNom} · {fmt(p.huileGrignon)} kg · valorisée {fmt(p.valeurEntree, 3)} TND
                    (CMP {fmt(p.cmpValorisation, 3)}) → proposé : 0 TND
                  </span>
                  <button
                    type="button"
                    data-testid={`rp-corriger-${p.id}`}
                    onClick={() => corriger(p.id)}
                    className="border border-amber-500 px-2 py-0.5 text-[11px] font-bold hover:bg-amber-100"
                  >
                    Appliquer la correction
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div data-testid="rp-periode" className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-white px-4 py-3 md:grid-cols-5">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Période d’origine — du (inclus)</label>
            <input
              data-testid="rp-periode-debut"
              type="datetime-local"
              value={rp.periodeDebut}
              onChange={(e) => setRp({ ...rp, periodeDebut: e.target.value })}
              className={inputCls}
            />
            <p className="mt-0.5 text-[10px] text-slate-500">
              {borneContinuite ? `Continuité : fin de la plage précédente ${versLocal(borneContinuite).replace('T', ' ')}` : 'Première utilisation : choisissez le point de départ'}
            </p>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">au (exclu)</label>
            <input
              data-testid="rp-periode-fin"
              type="datetime-local"
              value={rp.periodeFin}
              min={rp.periodeDebut}
              onChange={(e) => setRp({ ...rp, periodeFin: e.target.value })}
              className={inputCls}
            />
            <p className="mt-0.5 text-[10px] text-slate-500">Heures usine (Africa/Tunis) · intervalle [du, au[</p>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Olives triturées (kg)</label>
            <p data-testid="rp-olives-origine" className="tabular flex h-10 items-center border border-slate-200 bg-slate-100 px-3 font-data text-sm font-semibold text-slate-900">
              {fmt(production.kg)} kg
            </p>
            <p className="mt-0.5 text-[10px] text-slate-500">
              {production.lots
                ? `${production.lots} lot(s)${production.precisionHoraire ? '' : ' · Précision horaire indisponible'}`
                : 'Aucune production validée sur la plage'}
            </p>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Grignons estimés (t)</label>
            <p data-testid="rp-grignons-estimes" className="tabular flex h-10 items-center border border-slate-200 bg-slate-100 px-3 font-data text-sm font-semibold text-slate-900">
              {fmt(grignonsEstimes, 3)} t
            </p>
            <div className="mt-0.5 flex items-center gap-1">
              <span className="text-[10px] text-slate-500">coefficient</span>
              <input
                data-testid="rp-coefficient"
                type="number"
                step="0.01"
                min="0.1"
                max="1"
                value={rp.coefficientGrignon}
                onChange={(e) => setRp({ ...rp, coefficientGrignon: e.target.value })}
                className="h-6 w-16 border border-slate-300 bg-white px-1 font-data text-[11px]"
              />
              <span className="text-[10px] text-slate-500">estimation, pas une pesée</span>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Rendement huile / olives</label>
            <p data-testid="rp-rendement-olives" className="tabular flex h-10 items-center border-2 border-orange-600 bg-orange-50 px-3 font-data text-sm font-bold text-orange-800">
              {rdtRepassage != null ? `${fmt(rdtRepassage, 2)} %` : '—'}
            </p>
          </div>
        </div>
        <form onSubmit={submitRp} className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 md:grid-cols-5">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Date du repassage</label>
            <input data-testid="rp-date" type="date" value={rp.date} onChange={(e) => setRp({ ...rp, date: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Huile obtenue (kg)</label>
            <input data-testid="rp-huile" type="number" step="0.1" placeholder="Huile de grignon (kg)" value={rp.huileGrignon} onChange={(e) => setRp({ ...rp, huileGrignon: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Destination</label>
            <select data-testid="rp-cuve" value={rp.cuveId} onChange={(e) => setRp({ ...rp, cuveId: e.target.value })} className={inputCls}>
              <option value="">— Cuve lampante —</option>
              {cuvesLampantes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom} · reste {fmt(c.capacite - c.volume)} {c.unite}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Correction de période (motif)</label>
            <input data-testid="rp-motif-correction" value={motifCorrection} onChange={(e) => setMotifCorrection(e.target.value)} placeholder="Exceptionnel — tracé" className={inputCls} />
          </div>
          <button
            type="submit"
            data-testid="rp-submit"
            disabled={occupe}
            className="flex h-10 items-center justify-center gap-2 self-end bg-orange-600 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-orange-500 disabled:opacity-50"
          >
            <Plus size={15} /> {occupe ? 'Enregistrement…' : 'Verser en cuve'}
          </button>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-repassage">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date repassage', 'Période d’origine (Tunis)', 'Olives triturées (kg)', 'Grignons estimés (t)', 'Huile obtenue (kg)', 'Rendement / olives (%)', 'Cuve lampante', 'Coût matière (TND/kg)', 'Valeur matière entrée (TND)', 'Traçabilité', ''].map((c, i) => (
                  <th key={c || i} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sp.repassages.map((p) => (
                <tr key={p.id} data-testid={`repassage-row-${p.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${p.annuleLe ? 'opacity-50 line-through' : ''}`}>
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{p.date}</td>
                  <td className="px-3 py-2 font-data text-xs text-slate-700">
                    {p.periodeDebut
                      ? `${versLocal(p.periodeDebut).replace('T', ' ')} → ${versLocal(p.periodeFin).replace('T', ' ')}`
                      : p.dateDebut
                        ? `${p.dateDebut} → ${p.dateFin ?? p.dateDebut}`
                        : '—'}
                  </td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{p.olivesOrigine ? fmt(p.olivesOrigine) : '—'}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-900">
                    {p.grignonsEstimes != null ? fmt(p.grignonsEstimes, 3) : fmt(p.grignonsTraites, 3)}
                    {p.coefficientGrignon ? <span className="ml-1 text-[10px] text-slate-500">×{p.coefficientGrignon}</span> : null}
                  </td>
                  <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(p.huileGrignon)}</td>
                  <td data-testid={`repassage-rdt-${p.id}`} className="tabular px-3 py-2 font-data text-sm font-bold text-orange-800">
                    {p.rendementOlives != null ? fmt(p.rendementOlives, 2) : '—'}
                  </td>
                  <td className="px-3 py-2 font-data text-sm text-orange-800">{p.cuveNom}</td>
                  <td data-testid={`repassage-cout-${p.id}`} className="tabular px-3 py-2 font-data text-sm text-slate-900">
                    {fmt(p.coutMatiereUnitaire ?? p.cmpValorisation ?? 0, 3)}
                  </td>
                  <td data-testid={`repassage-valeur-${p.id}`} className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(p.valeurEntree ?? 0, 3)}</td>
                  <td className="px-3 py-2 text-[10px] text-slate-500">
                    {p.mouvementStock ? <span className="font-data">{p.mouvementStock}</span> : '—'}
                    {p.lotsRetenus?.length ? <span> · {p.lotsRetenus.length} lot(s)</span> : null}
                    {p.auteur ? <span> · {p.auteur}</span> : null}
                    {p.precisionHoraire === false ? <span className="text-amber-700"> · précision horaire indisponible</span> : null}
                    {p.motifCorrection ? <span className="text-amber-700"> · correction : {p.motifCorrection}</span> : null}
                    {p.valorisationCorrigeeLe ? <span className="text-emerald-700"> · valorisation corrigée</span> : null}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!p.annuleLe && (
                      <button
                        type="button"
                        data-testid={`rp-annuler-${p.id}`}
                        onClick={() => annuler(p.id)}
                        className="border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-900 hover:text-white"
                      >
                        Annuler
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        testid="section-emballages"
        icon={Boxes}
        accent="border-l-slate-400"
        titre="Stock d'emballages"
        sous="Articles de conditionnement vendus au comptoir"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-emballages">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Article', ...(argent ? ['Prix (TND)'] : []), 'Stock', ...(argent ? ['Valeur (TND)'] : [])].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => (
                <tr key={a.id} data-testid={`article-row-${a.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-sm text-slate-800">{a.nom}</td>
                  {argent && <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(a.prixEmballage, 3)}</td>}
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(a.stock)}</td>
                  {argent && <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(a.prixEmballage * a.stock, 3)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {argent && <FacturationSousProduits onDone={load} />}

      {bonPesee && (
        <PrintDoc
          type="Bon de pesée / sortie — grignons"
          numero={attribuerNumero('bon_pesee', bonPesee.id)}
          date={bonPesee.date}
          tiers={bonPesee.acheteur}
          lignes={[
            ['Nature', 'Sortie de grignons d’olive'],
            ['Transporteur', bonPesee.transporteur],
            ['Nombre de camions', fmt(bonPesee.camions)],
            ['Tonnage pesé', `${fmt(bonPesee.tonnage, 1)} T`],
            ['Statut', bonPesee.statut],
            ['Référence interne', bonPesee.id],
          ]}
          totaux={argent ? [['Montant', `${fmt(bonPesee.tonnage * bonPesee.prix, 3)} TND`]] : []}
          mentions="Bon de pesée à signer au départ du camion. Poids constaté sur pont-bascule."
          signatures={['Signature & cachet — Huilerie', 'Signature — Transporteur']}
          onClose={() => setBonPesee(null)}
        />
      )}
    </div>
  );
}
